import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ErrorState, LoadingState } from "./components/ui";
import { api, currentSession, setCsrfToken, type SessionUser } from "./lib/api";
import { AuthPage } from "./pages/AuthPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";

const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const OrdersPage = lazy(() => import("./pages/OrdersPage").then((module) => ({ default: module.OrdersPage })));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage").then((module) => ({ default: module.OrderDetailPage })));

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startupError, setStartupError] = useState("");
  async function loadSession() {
    setLoading(true);
    setStartupError("");
    try {
      const session = await currentSession();
      if (session) setUser(session.user);
      else {
        const state = await api<{ bootstrapRequired: boolean }>("/auth/session");
        setUser(null); setBootstrapRequired(state.bootstrapRequired);
      }
    } catch {
      setUser(null);
      setStartupError("The order database is temporarily unavailable. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void loadSession(); }, []);
  async function logout() { try { await api("/auth/logout", { method: "POST" }); } finally { setCsrfToken(""); setUser(null); } }
  if (loading) return <div className="boot-screen"><LoadingState label="Opening Razzi Order Desk" /></div>;
  if (startupError) return <div className="boot-screen"><ErrorState message={startupError} retry={() => void loadSession()} /></div>;
  if (!user) return <AuthPage bootstrapRequired={bootstrapRequired} onAuthenticated={() => void loadSession()} />;
  if (user.mustChangePassword && user.source === "local") return <ChangePasswordPage onDone={() => void loadSession()} />;
  const pendingOnly = user.permissions.includes("view_pending_orders") && !user.permissions.includes("view_orders");
  return <Suspense fallback={<LoadingState label="Opening workspace" />}><Routes><Route element={<Layout user={user} onLogout={() => void logout()} />}>
    <Route index element={pendingOnly ? <Navigate to="/pending" replace /> : <DashboardPage />} />
    <Route path="orders" element={pendingOnly ? <Navigate to="/pending" replace /> : <OrdersPage view="all" user={user} />} />
    <Route path="pending" element={<OrdersPage view="pending" user={user} />} />
    <Route path="dispatched" element={pendingOnly ? <Navigate to="/pending" replace /> : <OrdersPage view="dispatched" user={user} />} />
    <Route path="delivered" element={pendingOnly ? <Navigate to="/pending" replace /> : <OrdersPage view="delivered" user={user} />} />
    <Route path="returns" element={pendingOnly ? <Navigate to="/pending" replace /> : <OrdersPage view="returns" user={user} />} />
    <Route path="orders/:id" element={<OrderDetailPage user={user} />} />
    <Route path="*" element={<Navigate to={pendingOnly ? "/pending" : "/"} replace />} />
  </Route></Routes></Suspense>;
}
